<template>
  <div>
    <div v-if="props.label" class="form-label">{{ props.label }}</div>
    <transition-group tag="div">
      <div v-for="(location, index) in managedLocations" :key="index" class="d-flex align-items-center gap-2 mb-2">
        <div class="location-combobox-wrapper">
          <LeadCombobox
            :model-value="location"
            :items="options"
            :getOptionLabel="(option) => option.label"
            :getOptionValue="(option) => option.id"
            @update:model-value="updateLocation(index, $event)"
            @search="(q) => onSearch(q, index)"
            :loading="loadingByIndex[index] ?? false"
            :disable-filtering="true"
            placeholder="e.g. Switzerland" />
        </div>
        <LeadButton variant="outline-danger" class="location-remove-button" @click="removeLocation(index)">
          <template #icon>
            <IconTrashFilled size="16" />
          </template>
        </LeadButton>
      </div>
    </transition-group>
  </div>

  <div v-if="flattenedErrors.length > 0" class="my-2">
    <FormErrorList :errors="flattenedErrors" />
  </div>

  <div>
    <LeadButton variant="secondary" @click="addLocationEntry">
      {{ $t('btn.add_location') }}
    </LeadButton>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import LeadButton from '../generic/LeadButton.vue';
import { IconTrashFilled } from '@tabler/icons-vue';
import { FormErrorList } from '@prospective/lithium';
import { getCampaignOptions } from '@/api/campaign';
import LeadCombobox from './LeadCombobox.vue';
import { debounce } from '@prospective/lithium';
import { flatErrors } from '@/helpers/helpers';

export interface TikTokLocation {
  id: number;
  type: string;
  label: string;
  name: string;
}

const defaultLocation: TikTokLocation = { id: 0, type: '', label: '', name: '' };

const props = withDefaults(
  defineProps<{
    modelValue: TikTokLocation[];
    providerId: string | null | undefined;
    label?: string;
    errors?: any;
  }>(),
  {
    modelValue: () => [],
    errors: () => [],
  }
);

const flattenedErrors = computed<string[]>(() => flatErrors(props.errors));

const emit = defineEmits<{
  'update:modelValue': [value: TikTokLocation[]];
}>();

const options = ref<TikTokLocation[]>([]);
const loadingByIndex = ref<Record<number, boolean>>({});

const managedLocations = computed({
  get: () => {
    if (!props.modelValue || props.modelValue.length === 0) {
      return [{ ...defaultLocation }];
    }
    return props.modelValue;
  },
  set: (value: TikTokLocation[]) => {
    emit('update:modelValue', value);
  },
});

const addLocationEntry = () => {
  managedLocations.value = [...managedLocations.value, { ...defaultLocation }];
};

const removeLocation = (index: number) => {
  const newList = [...managedLocations.value];
  newList.splice(index, 1);
  managedLocations.value = newList;
};

const updateLocation = (index: number, newLocation: TikTokLocation) => {
  const newList = [...managedLocations.value];
  newList[index] = newLocation;
  managedLocations.value = newList;
};

const searchLocations = async (query: string, index: number) => {
  if (!query || query.length < 2) {
    options.value = [];
    loadingByIndex.value[index] = false;
    return;
  }
  try {
    const res = await getCampaignOptions(props.providerId ?? '', 'target_locations', { query });
    options.value = (res.data || []) as TikTokLocation[];
  } finally {
    loadingByIndex.value[index] = false;
  }
};

const searchLocationsDebounced = debounce(searchLocations, 400);

const onSearch = (query: string, index: number) => {
  loadingByIndex.value[index] = true;
  searchLocationsDebounced(query, index);
};
</script>

<style scoped>
.location-combobox-wrapper {
  flex: 1 1 auto;
  min-width: 0;
}

.location-remove-button {
  flex-shrink: 0;
}
</style>
